// Patient interaction time tracking — pure math + constants (no React, no I/O).
// Ported from wpc-front's auto-timer (always-on mode only). Billing-sensitive:
// every value here feeds Medicare CPT time entries — change with sign-off.

export const MIN_SAVE_SECONDS = 60;
export const FLUSH_INTERVAL_MS = 5 * 60_000;
export const FLUSH_CHUNK_SECONDS = 300;
// Auto-pause guard: a forgotten drawer must not bill unbounded wall-clock.
export const MAX_CONTINUOUS_SESSION_SECONDS = 4 * 3600;
// Crash-recovery clamp: a localStorage entry can never mint more than this.
export const MAX_RECOVERY_SECONDS = 30 * 60;
// Must sit far above Chrome's hidden-tab timer throttling (~1 fire/min) — a
// LIVE backgrounded session must never look claimable to another tab.
export const CACHE_STALE_MS = 5 * 60_000;
// A tick gap larger than this means the machine slept — the gap is spliced
// out, never billed (timers don't run during system suspend).
export const SUSPEND_GAP_MS = 3 * 60_000;
export const CACHE_MAX_AGE_MS = 24 * 3600_000;
export const MIN_MINUTES_LOG_TIME = 1;
export const MAX_MINUTES_LOG_TIME = 120;
// Time-based CPT codes: 99457 init 20 min, 99458 add'l 20 min, 99470 treatment mgmt.
// The chip's month total sums ONLY the 99457/99458 pathway: apiv2 credits every
// logged minute to 99470 IN PARALLEL (an alternative billing route), so summing
// all three doubles the real time (util/ProgramInteraction.php addTime).
export const PRIMARY_TIME_CPT_CODES = new Set([99457, 99458]);

// Staff Withings ids whose interaction timer starts PAUSED instead of running.
// These accounts browse patient files without the intent to bill (program
// coordinators reviewing the board), so wall-clock presence must not become a
// Medicare minute by default — they press play when the time is real work.
// Keyed on the Withings user id because that IS the `authorid` every time write
// records. Ids are opaque numerics, not PHI.
export const TIMER_STARTS_PAUSED_AUTHOR_IDS: ReadonlySet<string> = new Set<string>([
  '36437208',
  '7572654',
]);

export function timerStartsPausedFor(authorid: string | number | null | undefined): boolean {
  if (authorid == null || authorid === '') return false;
  return TIMER_STARTS_PAUSED_AUTHOR_IDS.has(String(authorid));
}

// Floors to the whole minute; sub-minute inputs are never saved (never rounds up).
export function roundSecondsWithThreshold(seconds: number): number | false {
  if (!Number.isFinite(seconds) || seconds < MIN_SAVE_SECONDS) return false;
  return Math.floor(seconds / 60) * 60;
}

export function formatTimer(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function formatMinutesLabel(totalSeconds: number): string {
  const minutes = Math.round(Math.max(0, totalSeconds) / 60);
  return `${minutes} min`;
}

export function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// 6-month read window for programinteraction.list (wpc-front parity).
export function cptWindow(now: Date = new Date()): { startdateymd: string; enddateymd: string } {
  const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startdateymd: ymd(start), enddateymd: ymd(end) };
}

export interface CptInteractionLike {
  code?: string | number;
  value?: string | number;
}
export interface CptMonthBucketLike {
  date?: string;
  interactions?: CptInteractionLike[];
}

// Sum of the current local month's time-CPT seconds. Bucket dates are
// 'YYYY-MM-DD' strings — compared by prefix, never via new Date() (UTC shift).
export function currentMonthCptSeconds(buckets: CptMonthBucketLike[] | null | undefined, now: Date = new Date()): number {
  const monthPrefix = ymd(now).slice(0, 7);
  const bucket = (buckets ?? []).find((b) => typeof b.date === 'string' && b.date.slice(0, 7) === monthPrefix);
  if (!bucket) return 0;
  return (bucket.interactions ?? [])
    .filter((i) => PRIMARY_TIME_CPT_CODES.has(Number(i.code)))
    .reduce((acc, i) => acc + (Number(i.value) || 0), 0);
}

export interface TimeEntryLike {
  id: string | number;
  time: string | number;
  date?: string;
  created?: string | number;
  manual?: boolean | number;
  author?: { id?: string | number; firstname?: string };
}

// Manual entries are dated by the user-picked `date`; auto entries by the
// server `created` timestamp (wpc-front rendering parity).
export function entryTimestampMs(entry: TimeEntryLike): number {
  if (entry.manual && entry.date) return new Date(`${entry.date.slice(0, 10)}T00:00:00`).getTime();
  if (entry.created) return Number(entry.created) * 1000;
  if (entry.date) return new Date(`${entry.date.slice(0, 10)}T00:00:00`).getTime();
  return 0;
}

export interface MonthGroup {
  monthKey: string;
  label: string;
  totalSeconds: number;
  entries: TimeEntryLike[];
}

export function groupEntriesByMonth(entries: TimeEntryLike[] | null | undefined): MonthGroup[] {
  const byMonth = new Map<string, MonthGroup>();
  (entries ?? []).forEach((entry) => {
    const ts = entryTimestampMs(entry);
    if (!ts) return;
    const d = new Date(ts);
    const monthKey = ymd(d).slice(0, 7);
    let group = byMonth.get(monthKey);
    if (!group) {
      group = {
        monthKey,
        label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        totalSeconds: 0,
        entries: [],
      };
      byMonth.set(monthKey, group);
    }
    group.totalSeconds += Number(entry.time) || 0;
    group.entries.push(entry);
  });
  const groups = [...byMonth.values()].sort((a, b) => (a.monthKey < b.monthKey ? 1 : -1));
  groups.forEach((g) => g.entries.sort((a, b) => entryTimestampMs(b) - entryTimestampMs(a)));
  return groups;
}

export interface TimerCacheEntry {
  tabId: string;
  startedAt: number;
  elapsedSeconds: number;
  flushedSeconds: number;
  lastTickAt: number;
  programid?: string;
  authorid?: string;
}

// A cache entry may be claimed for recovery only when it belongs to another
// tab AND its heartbeat is stale (a fresh lastTickAt means a live tab owns it).
export function isClaimableCacheEntry(entry: TimerCacheEntry | null | undefined, ownTabId: string, now: number): boolean {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.tabId === ownTabId) return false;
  if (!Number.isFinite(entry.lastTickAt)) return false;
  return now - entry.lastTickAt > CACHE_STALE_MS;
}

// Recoverable billable seconds from a crashed-tab cache entry. Every field is
// re-validated and the credit is wall-clock-bounded + clamped — a forged or
// corrupt entry can never mint more than MAX_RECOVERY_SECONDS.
export function computeRecoverableSeconds(entry: TimerCacheEntry | null | undefined, now: number): number {
  if (!entry || typeof entry !== 'object') return 0;
  const {
    startedAt, elapsedSeconds, flushedSeconds, lastTickAt,
  } = entry;
  if (![startedAt, elapsedSeconds, flushedSeconds, lastTickAt].every((n) => Number.isFinite(n) && (n as number) >= 0)) return 0;
  if (startedAt > lastTickAt || lastTickAt > now + 60_000) return 0;
  if (now - startedAt > CACHE_MAX_AGE_MS) return 0;
  const wallBound = (lastTickAt - startedAt) / 1000;
  const credit = Math.min(elapsedSeconds, wallBound) - flushedSeconds;
  return Math.max(0, Math.min(Math.floor(credit), MAX_RECOVERY_SECONDS));
}

export interface ManualEntryInput {
  minutes: unknown;
  dateYmd: string;
  minYmd?: string | null;
  maxYmd: string;
}

// The staff member's Withings id for scalews `authorid` (REQUIRED on
// programinteractiontime.create). account.getuserslist returns it as `id`
// (`userid` legacy alias; `accountid` is a DIFFERENT id) — PatientNotes bug class.
export function resolveAuthorId(currentUser: { id?: string | number; userid?: string | number } | null | undefined): string | undefined {
  const id = currentUser?.id ?? currentUser?.userid;
  return id != null && id !== '' ? String(id) : undefined;
}

// Returns an error message, or null when valid. 'YYYY-MM-DD' strings compare
// lexicographically, so the bounds check is a plain string compare.
export function validateManualEntry({
  minutes, dateYmd, minYmd, maxYmd,
}: ManualEntryInput): string | null {
  const m = Number(minutes);
  if (!Number.isInteger(m) || m < MIN_MINUTES_LOG_TIME || m > MAX_MINUTES_LOG_TIME) {
    return `Minutes must be a whole number between ${MIN_MINUTES_LOG_TIME} and ${MAX_MINUTES_LOG_TIME}.`;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateYmd)) return 'Pick a valid date.';
  if (dateYmd > maxYmd) return 'The date cannot be in the future.';
  if (minYmd && dateYmd < minYmd) return 'The date is before the patient joined the program.';
  return null;
}
